/**
 * ProjectClientRequestsSection.jsx — Phase 81
 *
 * Per-project client inbox. Lists client_requests where project_id === this
 * project, opens each thread with an inline reply composer. Reply hits
 * POST /api/client-requests/{rid}/replies which lands directly in the
 * client's portal — NO approval flow (per user spec).
 */
import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Inbox, Loader2, ChevronRight, ChevronDown, User } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function ProjectClientRequestsSection({ projectId }) {
  const [rows, setRows] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [loadError, setLoadError] = useState(null);  // Phase 82.2 — stop swallowing 403s silently

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/client-requests?project_id=${projectId}`);
      setRows(data || []);
      setLoadError(null);
    } catch (e) {
      setRows([]);
      const status = e?.response?.status;
      setLoadError(status === 403 ? "restricted" : "error");
    }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  if (!rows) return null;
  // Hide the panel entirely if the current user lacks read access — better
  // than misleading them with "No client requests on this project yet."
  if (loadError === "restricted") return null;

  return (
    <div className="card-flat overflow-hidden mt-6" data-testid="project-client-requests-section">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Inbox size={14} className="text-[#F54800]" />
          <div className="font-display font-semibold text-slate-900 text-sm">Client requests</div>
          <span className="text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-sm" data-testid="pcrs-count">
            {rows.length} thread{rows.length === 1 ? "" : "s"}
          </span>
        </div>
        <div className="text-[10px] text-slate-400">Replies post directly to the client portal</div>
      </div>
      {rows.length === 0 && (
        <div className="px-5 py-8 text-xs text-slate-400 italic text-center" data-testid="pcrs-empty">
          No client requests on this project yet.
        </div>
      )}
      <ul>
        {rows.map((r) => (
          <Thread
            key={r.id}
            row={r}
            open={openId === r.id}
            onToggle={() => setOpenId(openId === r.id ? null : r.id)}
            onReplied={load}
          />
        ))}
      </ul>
    </div>
  );
}

function Thread({ row: r, open, onToggle, onReplied }) {
  const [replies, setReplies] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get(`/client-requests/${r.id}/replies`).then(({ data }) => setReplies(data || [])).catch(() => setReplies([]));
  }, [open, r.id]);

  const submit = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/client-requests/${r.id}/replies`, { body: draft.trim() });
      setReplies((arr) => [...(arr || []), data]);
      setDraft("");
      toast.success("Reply sent — client will see it on their portal");
      onReplied?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const when = (r.last_reply_at || r.created_at || "").slice(0, 16).replace("T", " ");
  const badge = r.status === "new" ? "New" : r.status;
  return (
    <li className="border-b border-slate-50 last:border-0" data-testid={`pcrs-thread-${r.id}`}>
      <button
        onClick={onToggle}
        className="w-full px-5 py-3 flex items-start gap-2 hover:bg-slate-50/60 text-left"
        data-testid={`pcrs-thread-header-${r.id}`}
      >
        {open ? <ChevronDown size={13} className="text-slate-400 mt-1" /> : <ChevronRight size={13} className="text-slate-400 mt-1" />}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm text-slate-900 truncate">{r.title}</span>
            {r.status && (
              <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${r.status === "new" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{badge}</span>
            )}
            {r.reply_count > 0 && (
              <span className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-sm">{r.reply_count} repl{r.reply_count === 1 ? "y" : "ies"}</span>
            )}
          </div>
          <div className="text-[11px] text-slate-500 truncate">
            {r.client_name} · {r.request_type} · {when}
          </div>
        </div>
      </button>
      {open && (
        <div className="px-5 pb-4 space-y-2.5 bg-slate-50/30" data-testid={`pcrs-thread-body-${r.id}`}>
          <div className="p-3 bg-white rounded-sm border border-slate-100">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Original brief</div>
            <div className="text-sm text-slate-800 whitespace-pre-wrap">{r.brief}</div>
          </div>
          {replies === null && <div className="text-xs text-slate-400 italic">Loading replies…</div>}
          {(replies || []).map((rep) => (
            <div
              key={rep.id}
              className={`p-3 rounded-sm border ${rep.author_side === "client" ? "border-blue-100 bg-blue-50/40" : "border-slate-200 bg-white"}`}
              data-testid={`pcrs-reply-${rep.id}`}
            >
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 inline-flex items-center gap-1">
                <User size={9} /> {rep.author_name} · {rep.author_side === "client" ? "Client" : "Internal"} · {(rep.created_at || "").slice(0, 16).replace("T", " ")}
              </div>
              <div className="text-sm text-slate-800 whitespace-pre-wrap">{rep.body}</div>
            </div>
          ))}
          <div className="pt-1">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Type a reply to the client…"
              rows={3}
              className="w-full text-sm border border-slate-200 rounded-sm px-3 py-2 focus:ring-1 focus:ring-[#F54800] outline-none"
              data-testid={`pcrs-reply-input-${r.id}`}
            />
            <div className="flex items-center justify-between mt-1.5">
              <div className="text-[10px] text-slate-400">Sends directly — no approval step.</div>
              <Button
                size="sm"
                onClick={submit}
                disabled={busy || !draft.trim()}
                className="rounded-sm h-8 bg-[#F54800] hover:bg-[#D63E00] text-white"
                data-testid={`pcrs-reply-send-${r.id}`}
              >
                {busy ? <Loader2 size={11} className="mr-1 animate-spin" /> : null} Send to client
              </Button>
            </div>
          </div>
        </div>
      )}
    </li>
  );
}
